import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useCustomerAddress } from 'hooks';
import CurvedArrowLeft from 'components/shared/curved-arrow-left';
import useIsMobile from 'hooks/utils/useIsMobile';
import { useRouter } from 'next/router';
import { reformatCountryCodeToCountryName } from 'lib/utils/helpers';
import CountrySelect from './countrySelect';
import styles from './addressForm.module.scss';

const AddressForm = ({
  formType,
  addaddressformaccountpage,
  secondaddressbookaccountpage,
  currentAddress,
  setAddToggleForm,
  setUpdateToggleForm,
  customer,
}) => {
  const {
    firstNameField,
    lastNameField,
    postalCodeField,
    houseNumberField,
    additionField,
    streetField,
    cityField,
    countryField,
    disclaimerContent,
    addAddressButton,
  } = addaddressformaccountpage;
  const { locale } = useRouter();
  const { saveAddressButton, backToAddressesButton, shippingCountries } =
    secondaddressbookaccountpage;

  const [formState, setFormState] = useState({
    firstName: currentAddress?.firstName || '',
    lastName: currentAddress?.lastName || '',
    address1: currentAddress?.address1 || '',
    address2: currentAddress?.address2 || '',
    zip: currentAddress?.zip || '',
    province: currentAddress?.province || '',
    city: currentAddress?.city || '',
    country: currentAddress?.country || '',
    company: currentAddress?.company || '',
  });
  const [{ createAddress, updateAddress }, { response, loading, error }] = useCustomerAddress();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [predictions, setPredictions] = useState(false);
  const isMobile = useIsMobile();

  const onChange = async (postalCode, houseNumber) => {
    if (locale === 'nl') {
      const fetchAddress = await fetch(
        `/api/get-addresses-predictions?PostalCode=${postalCode}&HouseNumber=${houseNumber}`
      );
      const { data } = await fetchAddress.json();

      const address1 = data[0]?.Street;
      const city = data[0]?.City;

      setFormState({
        ...formState,
        address1,
        city,
      });
    }
  };

  useEffect(() => {
    const { zip, address2 } = formState;
    if (predictions && zip.length > 0 && address2 > 0) {
      const delayDebounceFn = setTimeout(() => {
        onChange(zip, address2);
        setPredictions(false);
      }, 1500);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [formState]);

  useEffect(() => {
    if (!error && response && formSubmitted) {
      setAddToggleForm(false);
      setFormState({
        address1: '',
        address2: '',
        city: '',
        company: '',
        country: '',
        firstName: '',
        lastName: '',
        province: '',
        zip: '',
      });
    }
  }, [error, response, formSubmitted]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        const { firstName, lastName, address1, address2, zip, province, city, country, company } =
          formState;
        if (formType === 'Create') {
          createAddress(customer.customerAccessToken, {
            address1,
            address2,
            city,
            company,
            country,
            firstName,
            lastName,
            province,
            zip,
          });
        } else {
          updateAddress(customer.customerAccessToken, currentAddress.id, {
            address1,
            address2,
            city,
            company,
            country,
            firstName,
            lastName,
            province,
            zip,
          });
        }
        setFormSubmitted(true);
        if (setUpdateToggleForm) setUpdateToggleForm(false);
      }}
    >
      <ul>
        <li>
          <label htmlFor="firstName" className={styles.labels}>
            {firstNameField?.text}*{' '}
          </label>
          <br />
          <input
            className={styles.updateInput}
            required
            name="firstName"
            id="firstName"
            onChange={e => setFormState({ ...formState, firstName: e.target.value })}
            value={formState?.firstName}
          />{' '}
        </li>
        <li>
          <label htmlFor="lastName" className={styles.labels}>
            {lastNameField?.text}*{' '}
          </label>
          <br />
          <input
            className={styles.updateInput}
            required
            name="lastName"
            id="lastName"
            onChange={e => {
              setFormState({ ...formState, lastName: e.target.value });
            }}
            value={formState?.lastName}
          />{' '}
        </li>
        <li>
          <label htmlFor="zip" className={styles.labels}>
            {postalCodeField?.text}*{' '}
          </label>
          <br />
          <input
            className={styles.updateInput}
            required
            name="zip"
            id="zip"
            type="text"
            onChange={e => {
              setPredictions(true);
              setFormState({ ...formState, zip: e.target.value });
            }}
            value={formState?.zip}
          />{' '}
        </li>
        <li>
          <label htmlFor="address2" className={styles.labels}>
            {houseNumberField?.text}*{' '}
          </label>
          <br />
          <input
            className={styles.updateInput}
            required
            name="address2"
            id="address2"
            onChange={e => {
              setPredictions(true);
              setFormState({ ...formState, address2: e.target.value });
            }}
            value={formState?.address2}
          />{' '}
        </li>
        <li>
          <label htmlFor="address1" className={styles.labels}>
            {streetField?.text}*{' '}
          </label>
          <br />
          <input
            className={styles.updateInput}
            required
            name="address1"
            id="address1"
            onChange={e => setFormState({ ...formState, address1: e.target.value })}
            value={formState?.address1}
          />{' '}
        </li>
        <li className={styles.cityInput}>
          <label htmlFor="city" className={styles.labels}>
            {cityField?.text}*{' '}
          </label>
          <br />
          <input
            className={styles.updateInput}
            required
            name="city"
            id="city"
            onChange={e => setFormState({ ...formState, city: e.target.value })}
            value={formState?.city}
          />
          <br />
          <span className={styles.cityDescription}>{disclaimerContent?.text}</span>
        </li>
        <li>
          <label htmlFor="country" className={styles.labels}>
            {countryField?.text}*{' '}
          </label>
          <br />
          <CountrySelect
            formState={formState}
            setFormState={setFormState}
            shippingCountries={shippingCountries?.texts}
            locale={locale}
          />
          {/* <input
            className={styles.updateInput}
            required
            name="country"
            id="country"
            onChange={e => setFormState({ ...formState, country: e.target.value })}
            value={formState?.country}
          />{' '} */}
        </li>

        <div>
          <button
            className={classNames(styles.submitButton, { [styles.loading]: loading })}
            type="submit"
          >
            {formType === 'Create' ? `${addAddressButton?.text}` : `${saveAddressButton?.text}`}
          </button>
          <br />
          {error && <span className={styles.error}>{error}</span>}
        </div>
        <br />
        {isMobile && (
          <button
            className={styles.cancel}
            type="button"
            onClick={() => {
              setAddToggleForm(false);
              setUpdateToggleForm(false);
            }}
          >
            <CurvedArrowLeft />{' '}
            <span className={styles.cancelContent}>{backToAddressesButton?.text}</span>
          </button>
        )}
      </ul>
    </form>
  );
};

export default AddressForm;
